<template>
  <div v-rect-loading="loading" class="interview-invitation">
    <div class="interview-header">
      <span class="title">{{ $t(`${i18nPrefix}.title`) }}</span>
      <HmPersonalInfo class="hm-info"></HmPersonalInfo>
      <Language class="language"/>
    </div>
    <div class="interview-container">
      <div class="left">
        <div class="title">{{ $t(`${i18nPrefix}.title`) }}</div>
        <div class="info">
          <div class="info-title">{{ $t(`${i18nPrefix}.label.information`) }}</div>
          <div v-for="(info, index) in interviewInfoList" class="info-item" :key="index">
            <div class="label">{{ info.label }}</div>
            <div class="value">{{ info.value }}</div>
          </div>
        </div>
      </div>
      <div class="content">
        <template v-if="isHmSelectInterviewTime">
          <div class="content-title">{{ $t(`${i18nPrefix}.label.${maxSelectScheduleCount === 1 ? 'singleTime' : 'selectTime'}`) }}</div>
          <div class="time-type-content">
            <InterviewDuration v-model="duration" ref="interviewDuration"/>
            <div class="time-zone">
              <span class="label">{{ $t(`${i18nPrefix}.label.timeZone`) }}</span>
              <el-select class="time-zone-selector" v-model="timeZone" filterable @change="handleTimezoneChange">
                <el-option
                  v-for="item in timeZoneList"
                  :key="item.id"
                  :label="item.value"
                  :value="item.key"
                >
                </el-option>
              </el-select>
            </div>
          </div>
          <div class="time-schedule-content">
            <InterviewCalendar v-model="selectedDate" :selectedInterviewDateList="selectedDateList"/>
            <InterviewSchedule
              class="interview-schedule"
              disabledTooltip
              :selectedDate="selectedDate"
              :hmScheduleList="hmScheduleList"
              :selectedDateTimeList="selectedDateTimeList"
              :durationMinutes="durationMinutes"
              @selectSchedule="handleSelectSchedule"
            />
          </div>
          <div class="time-range-content">
            <div class="title content-title">{{ $t(`${i18nPrefix}.label.interviewTime`) }}</div>
            <InterviewTimeRange
              v-for="(interviewDateTime, index) in selectedDateTimeList"
              ref="interviewDateTimeRange"
              :key="interviewDateTime.vid"
              :index="index"
              :durationMinutes="durationMinutes"
              :errorMessage="interviewDateTime.duplicate ? $t(`${i18nPrefix}.message.duplicateDateTime`) : ''"
              :interviewDateTime="interviewDateTime"
              @deleteTime="handleDeleteTime(index)"
              @change="handleTimeRangeChange(index, $event)"
              @endTimeClick="handleEndTimeClick"
            />
          </div>
        </template>
        <template v-else-if="isInterviewScheduled">
          <div class="content-title interviewed-time">{{ $t(`${i18nPrefix}.label.interviewTime`) }}</div>
          <div class="interview-time">
            <div class="interview-time-item is-selected">
              <div class="date">{{ formatDate(interviewedTime.startDate) }}</div>
              <div class="time-range">{{ formatDateTime(interviewedTime.startDate, interviewedTime.startTime, interviewedTime.endTime) }}</div>
            </div>
          </div>
        </template>
        <div v-else-if="isInterviewCanceled" class="canceled">{{ $t(`${i18nPrefix}.label.canceled`) }}</div>
        <template v-if="!isHmSelectInterviewTime && (isCandidateSelectInterviewTime || isSuggestNewTime)">
          <template v-if="interviewedTime">
            <div class="content-title hm-selected-time">{{ $t(`${i18nPrefix}.label.interviewTime`) }}</div>
            <div class="interview-time">
              <div class="interview-time-item is-selected">
                <div class="date">{{ formatDate(interviewedTime.startDate) }}</div>
                <div class="time-range">{{ formatDateTime(interviewedTime.startDate, interviewedTime.startTime, interviewedTime.endTime) }}</div>
              </div>
            </div>
          </template>
          <template v-if="showAvailableInterviewTime">
            <div class="content-title hm-selected-time">{{ interviewTimeLabel }}</div>
            <div class="interview-time">
              <div v-for="(time, index) in availableInterviewTimeList" class="interview-time-item" :key="index">
                <div class="date">{{ formatDate(time.startDate) }}</div>
                <div class="time-range">{{ formatDateTime(time.startDate, time.startTime, time.endTime) }}</div>
              </div>
            </div>
          </template>
        </template>
        <div v-if="!isHmSelectInterviewTime && (interviewStatus === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_URL || interviewStatus === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_EVENT)" class="teams-tip">
          {{ $t(`${i18nPrefix}.label.teamsTip`) }}
        </div>
        <div v-if="!hiddenBtn" class="btn-group">
          <el-button class="cancel-btn common-btn" @click="handleCancelInterview">{{ $t('common.btn.cancel') }}</el-button>
          <el-button v-if="showScheduleBtn" class="reschedule-btn common-btn" @click="handleReschedule">
            {{ $t(`${i18nPrefix}.btn.${isHmSuggestNewTime ? 'suggestNewTime' : 'reschedule'}`) }}
          </el-button>
          <el-button v-if="isHmSelectInterviewTime" class="confirm-btn" type="primary" @click="handleConfirm">{{ $t('common.btn.confirm') }}</el-button>
          <el-button v-else-if="interviewStatus === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_URL || interviewStatus === INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_EVENT" class="confirm-btn" type="primary" @click="handleSend">
            {{ $t(`${i18nPrefix}.btn.send`) }}
          </el-button>
        </div>
      </div>
    </div>
    <DialogWrapper
      :show.sync="dialog.show"
      :loading="dialog.loading"
      :title="$t(`${i18nPrefix}.label.cancelInterview`)"
      @save="confirmCancelInterview"
    >
    </DialogWrapper>
  </div>
</template>

<script>
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import InterviewDuration from '@/components/interview/interviewDuration.vue'
import InterviewSchedule from '@/components/interview/interviewSchedule.vue'
import InterviewCalendar from '@/components/interview/interviewCalendar.vue'
import InterviewTimeRange from '@/components/interview/interviewTimeRange.vue'
import interviewInvitationMixin from '@/views/hm/common/interviewInvitationMixin'
import Language from '@/views/hm/common/language.vue'
import HmPersonalInfo from '@/views/hm/pc/components/hmPersonalInfo.vue'

export default {
  components: {
    HmPersonalInfo,
    Language,
    DialogWrapper,
    InterviewTimeRange,
    InterviewCalendar,
    InterviewSchedule,
    InterviewDuration
  },
  mixins: [interviewInvitationMixin],
  created () {

  },
  methods: {}
}
</script>

<style scoped lang="scss">
.interview-invitation {
  .interview-header {
    height: 110px;
    background-color: #0b0d1a;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 100px;
    position: relative;

    .title {
      color: #fff;
      font-size: 28px;
      font-weight: 600;
    }

    .hm-info {
      margin-right: 10%;
    }

    .language {
      position: absolute;
      right: 5%;
      top: 25px;
      width: auto;
    }
  }

  .interview-container {
    display: flex;
    margin: 40px auto 0;
    width: 1040px;
    min-height: 690px;
    background: #ffffff;
    box-shadow: 0 0 12px 5px rgba(203, 203, 203, 0.23);
    border-radius: 10px;

    .left {
      border-right: 1px solid #e5e5e5;
      width: 330px;

      .title {
        padding: 40px 0 30px 40px;
        font-weight: 600;
        font-size: 20px;
        color: #000000;
        line-height: 28px;
        border-bottom: 1px solid #e5e5e5;
      }

      .info {
        padding-top: 25px;
        padding-left: 40px;

        .info-title {
          font-weight: 500;
          font-size: 16px;
          color: #000000;
          line-height: 22px;
        }

        .info-item {
          margin-top: 20px;

          .label {
            font-weight: 500;
            font-size: 12px;
            line-height: 16px;
            color: #999;
          }

          .value {
            margin-top: 4px;
            font-size: 14px;
            color: #000;
            line-height: 20px;
          }
        }
      }
    }

    .content {
      flex: 1;
      padding: 40px 30px;
      display: flex;
      flex-direction: column;

      .content-title {
        font-weight: 500;
        font-size: 14px;
        color: #000;
        line-height: 20px;

        &.hm-selected-time,
        &.interviewed-time {
          font-size: 16px;
          line-height: 22px;
        }
      }

      .time-type-content {
        margin: 22px 0;
        display: flex;
        justify-content: space-between;

        .time-zone {
          display: flex;
          align-items: center;

          .label {
            font-size: 12px;
            margin-right: 10px;
          }

          .time-zone-selector {
            width: 250px;

            ::v-deep {
              .el-input__inner {
                color: #000;
                font-size: 12px;
                border-radius: 2px;
                height: 30px;
                line-height: 30px;
                padding-left: 10px;
              }

              .el-input__icon {
                line-height: 30px;
              }
            }
          }
        }
      }

      .time-schedule-content {
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;

        .interview-schedule {
          width: 344px;

          ::v-deep {
            .schedule-table {
              grid-template-columns: repeat(3, 1fr);
            }
          }
        }
      }

      .time-range-content {
        margin-bottom: 48px;

        .content-title {
          margin-bottom: 22px;
        }

        .interview-time-range {
          & + .interview-time-range {
            margin-top: 10px;
          }
        }
      }

      .suggest-new-time,
      .canceled {
        font-size: 14px;
        line-height: 20px;
        color: #666;
        text-align: center;
      }

      .canceled {
        padding-top: 128px;
      }

      .teams-tip {
        padding: 0 20px;
        margin: 120px 90px 0 70px;
        height: 80px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background-color: #f2f2f2;
        border-radius: 10px;
      }

      .btn-group {
        display: flex;
        margin-top: auto;

        .common-btn {
          min-width: 160px;
          height: 48px;
          border-radius: 4px;
          border: 1px solid #0096cc;
          color: #0096cc;

          &:hover {
            background: rgba(0, 150, 204, 0.1);
          }
        }

        .confirm-btn {
          display: block;
          min-width: 160px;
          height: 48px;
          margin-left: auto;
        }

        .el-button {
          &:last-child {
            margin-left: auto;
          }
        }
      }

      .interview-time {
        padding-left: 70px;
        padding-right: 90px;
        margin-top: 58px;
        margin-bottom: 20px;

        .interview-time-item {
          width: 492px;
          height: 60px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          border-radius: 2px;
          border: 1px solid rgba(0, 150, 204, 0.3);
          color: #0096cc;
          box-sizing: border-box;
          cursor: pointer;

          &:hover,
          &.is-selected {
            background: rgba(0, 150, 204, 0.1);
            border-radius: 4px;
            border-width: 0;

            .date, .time-range {
              font-weight: 500;
            }
          }

          & + .interview-time-item {
            margin-top: 20px;
          }

          .date, .time-range {
            font-size: 14px;
            line-height: 20px;
          }
        }
      }
    }
  }
}
</style>
